/** Local scene-only capture seam. Never reads DOM text, storage, location or save data. */
export const PHOTO_CAPTURE_EVENT = 'ea:photo-capture';
export interface PhotoCaptureResult { ok: boolean; blob?: Blob; error?: string }
export interface PhotoCaptureRequest { reply(result: PhotoCaptureResult): void }
export function requestScenePhoto(target: EventTarget = window, timeoutMs = 15000): Promise<Blob> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => { finish(new Error('The current scene did not provide a photo. Try again after it loads.')); }, timeoutMs);
    const finish = (value: Blob | Error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (value instanceof Error) reject(value);
      else if (value.type !== 'image/png' || value.size === 0) reject(new Error('The scene returned no usable PNG. No photo was saved.'));
      else resolve(value);
    };
    const detail: PhotoCaptureRequest = { reply(result) {
      if (!result.ok || !result.blob) finish(new Error(result.error === 'scene-not-ready' ? 'The scene is not ready. Wait for it to load, then try again.' : 'The current scene could not be read. No photo was saved.'));
      else finish(result.blob);
    } };
    target.dispatchEvent(new CustomEvent<PhotoCaptureRequest>(PHOTO_CAPTURE_EVENT, { detail }));
  });
}
/** Redraw pixels into a fresh PNG, stripping input metadata, with the required game credit. */
export async function preparePhotoExport(scene: Blob): Promise<Blob> {
  const image = await createImageBitmap(scene);
  try {
    if (image.width < 2 || image.height < 2) throw new Error('The scene image is empty. No photo was saved.');
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    const footer = Math.max(32, Math.round(image.width * 0.055));
    canvas.height = image.height + footer;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Photo export is not supported by this browser.');
    ctx.drawImage(image, 0, 0);
    // A lost/cleared WebGL buffer commonly returns a uniform or fully transparent frame.
    const pixels = ctx.getImageData(0, 0, image.width, image.height).data;
    let visible = false, varied = false;
    for (let i = 0; i < pixels.length; i += 4) {
      if (pixels[i + 3] > 0) visible = true;
      if (pixels[i] !== pixels[0] || pixels[i + 1] !== pixels[1] || pixels[i + 2] !== pixels[2] || pixels[i + 3] !== pixels[3]) varied = true;
      if (visible && varied) break;
    }
    if (!visible || !varied) throw new Error('The scene returned a blank frame. Try again after the scene loads.');
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, image.height, canvas.width, footer);
    ctx.fillStyle = '#000000'; ctx.font = `${Math.max(12, Math.round(footer * 0.45))}px sans-serif`;
    ctx.textBaseline = 'middle'; ctx.fillText('Exotics Adventure', 12, image.height + footer / 2, Math.max(1, canvas.width - 24));
    return await new Promise<Blob>((resolve, reject) => { canvas.toBlob(blob => { if (blob) resolve(blob); else reject(new Error('PNG export failed. No photo was saved.')); }, 'image/png'); });
  } finally { image.close(); }
}
